// React
import { useState, useEffect } from "react"

// UI
import { cn } from "@/lib/utils"
import { Heading } from "@/components/Typography"
import { CardWrapper } from "@/components/Card"
import { OverallStatsBlock } from "./OverallStatsBlock"

interface Testimonial {
	name: string
	role: string
	quote: string
	avatar: string
}

const testimonials: Testimonial[] = [
	{
		name: "Bart Jansen",
		role: "Investeerder",
		quote: "Goede app. Makkelijk om te investeren. Goed rendement.",
		avatar: "/images/avatars/avatar1.jpg",
	},
	{
		name: "Rik",
		role: "Investeerder",
		quote: "Zonhub biedt geweldige mogelijkheden om een bijdrage te leveren aan de energietransitie die ook nog eens een leuk rendement opleveren.",
		avatar: "/images/avatars/avatar2.jpg",
	},
	{
		name: "Investeerder",
		role: "Deelnemer",
		quote: "Zonhub is een inspirerende organisatie die de energietransitie echt verder helpt en de deelnemers ook nog rendement veel hoger dan spaarrente geeft. Win win!",
		avatar: "/images/avatars/avatar3.jpg",
	},
	{
		name: "International Investor",
		role: "Investeerder",
		quote: "A good investment, which also helps the environment. I switched my energy supplier to one of their partner vendors and have made 5 investments in different sites. A case of putting my money where my mouth is.",
		avatar: "/images/avatars/avatar4.jpg",
	},
	{
		name: "Langjarige Deelnemer",
		role: "Investeerder",
		quote:
			"Al heel wat jaar ben ik een tevreden deelnemer van Zonhub. Ik heb in een aantal projecten een klein bedrag geïnvesteerd wat " +
			"langzaam, met rente en opbrengsten, terugbetaald wordt. Ik vind het een mooi initiatief om af en toe wat in te investeren.",
		avatar: "/images/avatars/avatar5.jpg",
	},
	{
		name: "Klimaatbewuste Investeerder",
		role: "Investeerder",
		quote:
			"Ik kan thuis weinig doen aan klimaatsverbetering (oud huis al veel geïnvesteerd maar wilde meer bijdragen aan het klimaat) " +
			"en dit is de ideale manier. Bovendien wordt het financieel aantrekkelijk gemaakt. Eenvoudig, goede inzage, goede communicatie.",
		avatar: "/images/avatars/avatar6.jpg",
	},
	{
		name: "CO2 Compensator",
		role: "Investeerder",
		quote:
			"Zonhub is een eerlijke club, duidelijk in wat je kunt verwachten en wat de (ecologische) opbrengst van je investering is. " +
			"Zelf compenseer ik mijn vluchten met een investering in zonhub, een eerlijke en interessante wijze van co-2 compensatie.",
		avatar: "/images/avatars/avatar7.jpg",
	},
]

export function TestimonialCarousel({
	title,
	stats,
	loading = false,
}: {
	title: string
	stats: Array<{ title: string; value: string }>
	loading?: boolean
}) {
	const [currentIndex, setCurrentIndex] = useState(0)
	const [isAnimating, setIsAnimating] = useState(false)

	useEffect(() => {
		if (!loading) {
			const interval = setInterval(() => {
				setIsAnimating(true)
				setTimeout(() => {
					setCurrentIndex(
						(prevIndex) => (prevIndex + 1) % testimonials.length,
					)
					setIsAnimating(false)
				}, 300)
			}, 4000)

			return () => clearInterval(interval)
		}
	}, [loading])

	const currentTestimonial = testimonials[currentIndex]

	return (
		<div>
			<Heading as="h3" className="mb-2 text-2xl font-semibold">
				{title}
			</Heading>
			<div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
				<div className="flex flex-col space-y-4">
					<CardWrapper className="relative flex h-48 items-center justify-center rounded-lg p-6">
						{loading ? (
							<div className="flex w-full animate-pulse items-center gap-6">
								<div className="shrink-0">
									<div className="size-20 rounded-full bg-gray-200"></div>
								</div>
								<div className="flex-1 space-y-2">
									<div className="h-4 w-3/4 rounded bg-gray-200"></div>
									<div className="h-4 w-2/3 rounded bg-gray-200"></div>
									<div className="mt-3 space-y-1">
										<div className="h-3 w-1/3 rounded bg-gray-200"></div>
										<div className="h-3 w-1/4 rounded bg-gray-200"></div>
									</div>
								</div>
							</div>
						) : (
							<>
								<div
									className={cn(
										"flex w-full items-center gap-6 transition-all duration-300",
										isAnimating
											? "scale-95 opacity-0"
											: "scale-100 opacity-100",
									)}
								>
									{/* Profile Picture */}
									<div className="shrink-0">
										<div className="relative size-20 overflow-hidden rounded-full bg-gradient-to-br from-primary-400 to-primary-600 p-0.5 shadow-md">
											<div className="size-full overflow-hidden rounded-full bg-white">
												{/* Placeholder initials as fallback */}
												<div className="flex size-full items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200 text-2xl font-semibold text-brandgray-700">
													{currentTestimonial.name
														.split(" ")
														.map((n) => n[0])
														.join("")}
												</div>
											</div>
										</div>
									</div>

									{/* Quote Content */}
									<div className="flex-1">
										<div className="relative">
											<p className="text-sm italic text-brandgray-700 md:text-base">
												{currentTestimonial.quote}
											</p>

											<div className="mt-3">
												<p className="text-sm font-semibold text-gray-900">
													{currentTestimonial.name}
												</p>
												<p className="text-xs text-gray-700">
													{currentTestimonial.role}
												</p>
											</div>
										</div>
									</div>
								</div>

								{/* Dots indicator */}
								<div className="absolute bottom-3 left-1/2 mt-2 flex -translate-x-1/2 gap-1.5">
									{testimonials.map((_, index) => (
										<button
											key={index}
											className={cn(
												"flex items-center justify-center p-2",
											)}
											onClick={() => {
												setIsAnimating(true)
												setTimeout(() => {
													setCurrentIndex(index)
													setIsAnimating(false)
												}, 300)
											}}
											aria-label={`Go to testimonial ${index + 1}`}
										>
											<span
												className={cn(
													"size-2 rounded-full transition-all duration-300",
													index === currentIndex
														? "w-6 bg-primary-600"
														: "bg-primary-300 hover:bg-primary-500",
												)}
											/>
										</button>
									))}
								</div>
							</>
						)}
					</CardWrapper>
				</div>

				{/* Stats section */}
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					{loading
						? [0, 1].map((index) => (
								<OverallStatsBlock
									key={index}
									title=""
									stats=""
									loading={true}
								/>
							))
						: stats.map((stat, index) => (
								<OverallStatsBlock
									key={index}
									title={stat.title}
									stats={stat.value}
								/>
							))}
				</div>
			</div>
		</div>
	)
}
